/**
 * The only place this application talks to the API.
 *
 * ## Where the credential lives
 *
 * Nowhere in this file, and nowhere in JavaScript. The API sets an `HttpOnly`
 * session cookie scoped to the parent domain, so `admin.teslmsec.com` and
 * `api.teslmsec.com` share it; `credentials: "include"` is what tells the browser
 * to attach it to a cross-origin request. Nothing here reads or writes a token.
 *
 * That is the whole reason `credentials: "include"` appears rather than an
 * `Authorization` header. The prototype kept its JWT in `localStorage` and set
 * the header by hand, which is why its "sign out" was a `localStorage.clear()`
 * that left a thirty-day credential valid on the server, and why any XSS on the
 * page could lift it. The login response still carries a token, for the mobile
 * app and server-to-server callers — **this client deliberately ignores it.**
 *
 * ## Why every read goes through one function
 *
 * `read()` centralises four things that were wrong in four different places in
 * the prototype: the base URL, date revival, the 401 redirect, and the error
 * shape. A page calls `listEmployees(filters)`; it never sees a URL.
 */

import { AUTH_NOTICE_KEY, SESSION_EXPIRED_EVENT, type SessionExpiryReason } from "@/constants/events";
import { errors as errorCopy } from "@/content";
import { getActiveLocale } from "@/utils/locale";

/**
 * Locale for API messages. Sent as a header because the console cookie is
 * host-only on the admin origin and is not attached to cross-origin API calls.
 */
function localeHeaders(): Record<string, string> {
  return { "x-teslm-locale": getActiveLocale() };
}

/**
 * The API origin, injected at build time.
 *
 * `VITE_API_BASE_URL` is already set per environment in the repository's
 * GitHub environments — `https://api.dev.teslmsec.com`,
 * `https://api.staging.teslmsec.com`, `https://api.teslmsec.com` — and the deploy
 * workflows already pass it. Nothing new is needed from DevOps for this.
 *
 * Build-time, not runtime: this is a static bundle, so the value is baked into
 * the JavaScript. Changing it means a rebuild, not a restart.
 */
function apiOrigin(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const origin = configured?.trim().replace(/\/$/, "");

  if (!origin) {
    /* Thrown rather than defaulted. A console silently pointing at the wrong API
       is worse than one that refuses to start, because the first symptom would be
       an empty employees list that looks like real data. */
    throw new Error(
      "VITE_API_BASE_URL is not set. The console cannot reach the API without it.",
    );
  }
  return origin;
}

/** Matches an ISO instant, and deliberately nothing looser. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

/**
 * Turns ISO instants back into `Date` objects, in place, recursively.
 *
 * The pattern is strict on purpose. A date *key* like `2026-08-24` also looks
 * like a date, and converting it would produce a `Date` at midnight UTC — which
 * in Riyadh is 03:00 the same morning, but formatted back as a local day could
 * land a shift on the wrong date entirely. Rota cells and report buckets are
 * keyed by those strings, so they have to stay strings.
 */
function reviveDates<T>(value: T): T {
  if (typeof value === "string") {
    return (ISO_INSTANT.test(value) ? new Date(value) : value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(reviveDates) as T;
  }
  if (value && typeof value === "object") {
    for (const [key, entry] of Object.entries(value)) {
      (value as Record<string, unknown>)[key] = reviveDates(entry);
    }
  }
  return value;
}

export type Result<T> =
  | { ok: true; data: T; message?: string }
  | { ok: false; error: string; fieldErrors?: Record<string, string> };

/** Thrown by `read` so react-query can put a page into its error state. */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type QueryValue = string | number | boolean | null | undefined;

/** Drops empty values so the URL carries only filters that are actually set. */
function toSearch(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

/**
 * A 401 means the session is gone — revoked, expired, superseded by another
 * login, or the account deactivated. Announced as an event rather than
 * redirecting from here, because this module has no router: the session
 * provider listens and sends the operator to the sign-in screen, keeping one
 * place that decides what "signed out" looks like.
 */
function announceExpiry(reason: SessionExpiryReason = "expired"): void {
  if (reason === "session_superseded") {
    try {
      sessionStorage.setItem(AUTH_NOTICE_KEY, "session_superseded");
    } catch {
      /* Private mode can throw; the generic expired copy still applies. */
    }
  }
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }),
  );
}

/** Parse a 401 JSON body for a known session-failure code. */
async function expiryReasonFrom(response: Response): Promise<SessionExpiryReason> {
  const body = (await response.clone().json().catch(() => null)) as
    | { error?: string; ok?: boolean }
    | null;
  if (body?.error === "session_superseded") return "session_superseded";
  return "expired";
}

/** True for gateway / upstream faults the ALB surfaces when the API is down. */
function isTransientHttpStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

/**
 * The one machine code the API answers with on a 500 — every other failure it
 * sends is already a localised sentence. Humanised here, once, so the login
 * alert, every toast and every error state say something an operator can act
 * on. The server's request id is kept in the message: it is the only thing
 * support needs from a screenshot, because it finds the matching log line.
 */
function humanise<T>(body: Result<T>): Result<T> {
  if (body.ok === false && body.error === "internal_error") {
    const requestId = (body as { ok: false; requestId?: string }).requestId;
    return {
      ...body,
      error:
        errorCopy.internalError +
        (requestId ? ` ${errorCopy.internalErrorRequest} ${requestId}` : "") +
        ".",
    };
  }
  if (body.ok === false && body.error === "capacity_full") {
    return {
      ...body,
      error: errorCopy.capacityFull,
    };
  }
  return body;
}

/**
 * A read. Throws on failure, which is what react-query wants — it turns a thrown
 * error into an error state, and a resolved value into data.
 */
export async function read<T>(
  name: string,
  params: Record<string, QueryValue> = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiOrigin()}/api/v1/read/${name}${toSearch(params)}`, {
      credentials: "include",
      headers: { accept: "application/json", ...localeHeaders() },
    });
  } catch {
    throw new ApiError(errorCopy.networkUnavailable, 0);
  }

  if (response.status === 401) {
    announceExpiry(await expiryReasonFrom(response));
    throw new ApiError("unauthenticated", 401);
  }

  const body = (await response.json().catch(() => null)) as Result<T> | null;

  if (!response.ok || !body || body.ok !== true) {
    if (isTransientHttpStatus(response.status)) {
      throw new ApiError(errorCopy.networkUnavailable, response.status);
    }
    const failure = body ? humanise(body) : null;
    const message =
      failure && failure.ok === false ? failure.error : `HTTP ${response.status}`;
    throw new ApiError(message, response.status);
  }

  return reviveDates(body.data);
}

/**
 * An unauthenticated GET against a public mobile path.
 *
 * These routes have no console session, so a 401 here is not an expired
 * operator cookie and must not send the page to `/login`.
 */
export async function publicGet<T>(path: string): Promise<T> {
  const url = `${apiOrigin()}${path.startsWith("/") ? path : `/${path}`}`;
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: "omit",
      headers: { accept: "application/json", ...localeHeaders() },
    });
  } catch {
    throw new ApiError(errorCopy.networkUnavailable, 0);
  }

  const body = (await response.json().catch(() => null)) as T | null;
  if (!response.ok || body === null) {
    throw new ApiError(errorCopy.networkUnavailable, response.status);
  }
  return reviveDates(body);
}

/**
 * A write. **Never throws** — it returns the API's own result, `fieldErrors` and
 * all.
 *
 * That chain is the fix for the prototype's single
 * `catch { setError(t('common.error')) }`, which collapsed `email_in_use`,
 * `invalid_supervisor`, `missing_fields` and `upload_error` into one "something
 * went wrong". Duplicate email was by far the likeliest real failure, and it was
 * indistinguishable from a server fault. Its stylesheet even carried a
 * `.field-error` class for the per-field messages that were never built.
 */
export async function operate<T>(
  name: string,
  payload: Record<string, unknown>,
): Promise<Result<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/op/${name}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...localeHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      const reason = await expiryReasonFrom(response);
      announceExpiry(reason);
      return {
        ok: false,
        error:
          reason === "session_superseded"
            ? errorCopy.sessionSuperseded
            : errorCopy.sessionExpired,
      };
    }

    const body = (await response.json().catch(() => null)) as Result<T> | null;
    if (!body) return { ok: false, error: errorCopy.operationFailed };
    return humanise(body);
  } catch {
    /* A network failure, not a refusal. Reported as a failure the operator can
       retry rather than thrown, so a dropped connection mid-form does not lose
       what they typed. */
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

// ---------------------------------------------------------------------------
// Auth. Separate from read/op because these are the only calls that work — and
// have to work — without a session.
// ---------------------------------------------------------------------------

export async function requestLogin(
  identifier: string,
  password: string,
): Promise<Result<{ expiresAt: string }>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/login`, {
      method: "POST",
      /* Included on login above all: this is the response that carries the
         `Set-Cookie`, and without it the browser discards the session. */
      credentials: "include",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...localeHeaders(),
      },
      body: JSON.stringify({ identifier, password }),
    });

    const body = (await response.json().catch(() => null)) as Result<{
      expiresAt: string;
      token?: string;
    }> | null;

    if (!body) return { ok: false, error: errorCopy.loginFailed };
    /* `token` is present in the body and is intentionally not read. It exists for
       the mobile app; storing it here would put a credential in reach of script
       and undo the reason for the cookie. */
    return humanise(body) as Result<{ expiresAt: string }>;
  } catch {
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

export async function requestLogout(): Promise<void> {
  /* Failure is ignored on purpose. The cookie is cleared by the API, and if the
     call cannot be made the local session is dropped anyway — refusing to let
     somebody sign out because the network is down is not a safety measure.
     Cap the wait so a hung API cannot stall anything that still awaits this. */
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 4_000);
  try {
    await fetch(`${apiOrigin()}/api/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { ...localeHeaders() },
      signal: controller.signal,
    });
  } catch {
    /* network / abort — local sign-out already happened */
  } finally {
    window.clearTimeout(timer);
  }
}

/** The signed-in user and their settings, or a definitive "no session". */
export type SessionLookup<T> =
  | { kind: "session"; data: T }
  | { kind: "none" }
  /**
   * The API (or the network) could not answer. Must not be treated as signed-out:
   * a 502 from the load balancer while the cookie is still valid was logging
   * operators out mid-shift.
   */
  | { kind: "unavailable" };

/**
 * Probes `GET /auth/session`.
 *
 * Distinguishes "definitely not signed in" from "could not reach the API" so a
 * transient 502/network blip cannot clear a still-valid cookie session.
 */
export async function lookupSession<T>(): Promise<SessionLookup<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/session`, {
      credentials: "include",
      headers: { accept: "application/json", ...localeHeaders() },
    });

    if (response.status === 401) return { kind: "none" };
    if (!response.ok) return { kind: "unavailable" };

    const body = (await response.json().catch(() => null)) as Result<T> | null;
    if (!body) return { kind: "unavailable" };
    if (body.ok) return { kind: "session", data: reviveDates(body.data) };
    if (body.error === "unauthenticated") return { kind: "none" };
    return { kind: "unavailable" };
  } catch {
    return { kind: "unavailable" };
  }
}

/**
 * @deprecated Prefer `lookupSession` — kept for call sites that only need the
 * authenticated payload and already treat thrown/empty as "try again".
 */
export async function fetchSession<T>(): Promise<T | null> {
  const result = await lookupSession<T>();
  if (result.kind === "session") return result.data;
  if (result.kind === "none") return null;
  throw new ApiError(errorCopy.networkUnavailable, 503);
}

/**
 * The recovery endpoints — forgot password, invite acceptance, reset.
 *
 * One unauthenticated route with a `step`, matching the API. Kept out of
 * `operate` because these run with no session at all, and a 401 from here means
 * "bad token", not "signed out" — routing it through the expiry event would
 * bounce somebody off the very page that is meant to let them back in.
 */
export async function recover<T>(
  step: "request" | "set" | "request-otp" | "verify-otp" | "resend-otp",
  payload: Record<string, unknown>,
): Promise<Result<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/recover?step=${step}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...localeHeaders(),
      },
      body: JSON.stringify(payload),
    });
    const body = (await response.json().catch(() => null)) as Result<T> | null;
    return body ? humanise(body) : { ok: false, error: errorCopy.operationFailed };
  } catch {
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

/**
 * Inspects an invite or reset token before showing the form.
 *
 * The purpose is not sent — the API reads it off the token and reports it back,
 * which is what lets a reset link opened on the invite screen be recognised as
 * the wrong link rather than silently accepted.
 */
export async function inspectLink<T>(token: string): Promise<Result<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/recover${toSearch({ token })}`, {
      credentials: "include",
      headers: { accept: "application/json", ...localeHeaders() },
    });
    const body = (await response.json().catch(() => null)) as Result<T> | null;
    return body ?? { ok: false, error: errorCopy.linkInvalid };
  } catch {
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

/**
 * Prefill for a rejected applicant's resubmit form (`GET /auth/resubmit`).
 */
export async function inspectResubmitLink<T>(token: string): Promise<Result<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/resubmit${toSearch({ token })}`, {
      credentials: "include",
      headers: { accept: "application/json", ...localeHeaders() },
    });
    const body = (await response.json().catch(() => null)) as Result<T> | null;
    return body ?? { ok: false, error: errorCopy.linkInvalid };
  } catch {
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

/**
 * Saves corrected applicant fields and returns the account to Pending.
 */
export async function submitResubmit<T>(payload: Record<string, unknown>): Promise<Result<T>> {
  try {
    const response = await fetch(`${apiOrigin()}/api/v1/auth/resubmit`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...localeHeaders(),
      },
      body: JSON.stringify(payload),
    });
    const body = (await response.json().catch(() => null)) as Result<T> | null;
    return body ? humanise(body) : { ok: false, error: errorCopy.operationFailed };
  } catch {
    return { ok: false, error: errorCopy.networkUnavailable };
  }
}

/**
 * A URL on the API for the browser to navigate to or embed directly.
 *
 * There are no proxy routes in this application any more, and that is a direct
 * consequence of the cookie. The server-rendered version needed three — an
 * `EventSource` cannot set an `Authorization` header, and neither can a download
 * link or an `<img src>`. A cookie needs none of that: it is attached
 * automatically to a subresource request and to a top-level navigation, so the
 * browser can talk to `/api/stream`, `/api/export/…` and `/api/files/…` itself.
 */
export function apiUrl(path: string, params: Record<string, QueryValue> = {}): string {
  return `${apiOrigin()}${path}${toSearch(params)}`;
}

/** `listParams` in the shape the paged reads expect. */
export function listParams(input: {
  search?: string;
  sort?: { column: string; direction: "asc" | "desc" };
  page?: number;
  pageSize?: number;
}): Record<string, QueryValue> {
  return {
    q: input.search || undefined,
    sort: input.sort
      ? `${input.sort.direction === "desc" ? "-" : ""}${input.sort.column}`
      : undefined,
    page: input.page,
    size: input.pageSize,
  };
}
